import type {ArenaBox,ArenaDefinition} from './arena.ts';

export const TERRAIN={size:2,layer:1,hp:30,maxDepth:3,minX:-14,maxX:16,minZ:-8,maxZ:8} as const;
export interface TerrainDamage {hp:number;depth:number}
export interface TerrainCell {id:string;ix:number;iz:number;x:number;z:number;destructible:boolean}
const definitions=new WeakMap<ArenaDefinition,readonly TerrainCell[]>();

/** Only the interior plateau erodes; banks, structures and elevated routes keep foundations. */
export function terrainCells(arena:ArenaDefinition):readonly TerrainCell[]{
  if(arena.theme!=='inferno')return [];
  let cached=definitions.get(arena);if(cached)return cached;
  const cells:TerrainCell[]=[];
  for(let iz=0;iz<(TERRAIN.maxZ-TERRAIN.minZ)/TERRAIN.size;iz++)for(let ix=0;ix<(TERRAIN.maxX-TERRAIN.minX)/TERRAIN.size;ix++){
    const x=TERRAIN.minX+(ix+.5)*TERRAIN.size,z=TERRAIN.minZ+(iz+.5)*TERRAIN.size,r=TERRAIN.size/2;
    const overlaps=(x0:number,x1:number,z0:number,z1:number)=>x+r>x0-.5 && x-r<x1+.5 && z+r>z0-.5 && z-r<z1+.5;
    const foundation=(arena.props??[]).some(p=>p.blocks && overlaps(p.x-p.size.x/2,p.x+p.size.x/2,p.z-p.size.z/2,p.z+p.size.z/2)) ||
      arena.solids.some(b=>b.kind!=='floor' && !b.id.startsWith('prop:') && overlaps(b.min.x,b.max.x,b.min.z,b.max.z));
    cells.push({id:`${ix}:${iz}`,ix,iz,x,z,destructible:!foundation});
  }
  definitions.set(arena,cells);return cells;
}

export function terrainCellAt(arena:ArenaDefinition,x:number,z:number):TerrainCell|undefined{
  const ix=Math.floor((x-TERRAIN.minX)/TERRAIN.size),iz=Math.floor((z-TERRAIN.minZ)/TERRAIN.size);
  return terrainCells(arena).find(c=>c.ix===ix && c.iz===iz);
}

/** Each deeper layer requires a surrounding step, with at most one layer lost per impact. */
export function damageTerrain(cells:readonly TerrainCell[],state:{get(id:string):TerrainDamage|undefined},ids:readonly string[],amount:number):void{
  if(!Number.isFinite(amount)||amount<=0)return;
  const struck=cells.filter(c=>c.destructible && ids.includes(c.id));
  for(const c of struck){const s=state.get(c.id);if(s && s.depth<TERRAIN.maxDepth)s.hp=Math.max(0,s.hp-amount);}
  const first=new Set<string>();
  for(const c of struck){const s=state.get(c.id);if(s && s.depth===0 && s.hp===0){s.depth=1;s.hp=TERRAIN.hp;first.add(c.id);}}
  for(const c of struck){
    const s=state.get(c.id);if(!s || first.has(c.id) || s.depth>=TERRAIN.maxDepth || s.hp>0)continue;
    const ring=[[1,0],[-1,0],[0,1],[0,-1]].every(([dx,dz])=>(state.get(`${c.ix+dx!}:${c.iz+dz!}`)?.depth??0)>=s.depth);
    if(ring){s.depth++;s.hp=s.depth===TERRAIN.maxDepth?0:TERRAIN.hp;}else s.hp=1;
  }
}

/** Original floor strips outside the rectangular excavation area. */
export function terrainOuterFloors(arena:ArenaDefinition):ArenaBox[]{
  const out:ArenaBox[]=[];
  for(const b of arena.solids){
    if(b.kind!=='floor')continue;
    if(b.max.z<=TERRAIN.minZ || b.min.z>=TERRAIN.maxZ){out.push(b);continue;}
    if(b.min.x<TERRAIN.minX)out.push({...b,id:`${b.id}-west`,max:{...b.max,x:TERRAIN.minX}});
    if(b.max.x>TERRAIN.maxX)out.push({...b,id:`${b.id}-east`,min:{...b.min,x:TERRAIN.maxX}});
  }
  return out;
}

const liveCache=new WeakMap<ArenaDefinition,{key:string;arena:ArenaDefinition}>();
/** Identical floor heights in authoritative physics and client prediction. */
export function withTerrain(arena:ArenaDefinition,states:Iterable<[string,TerrainDamage]>):ArenaDefinition{
  if(arena.theme!=='inferno')return arena;
  const depths=new Map([...states].filter(([,s])=>s.depth>0).map(([id,s])=>[id,Math.min(TERRAIN.maxDepth,s.depth)]));
  const key=[...depths].map(([id,d])=>`${id}=${d}`).join(',');
  const old=liveCache.get(arena);if(old?.key===key)return old.arena;
  const floors=terrainCells(arena).map(c=>({id:`terrain:${c.id}`,kind:'floor' as const,
    min:{x:c.x-1,y:-4,z:c.z-1},max:{x:c.x+1,y:depths.has(c.id)?-depths.get(c.id)!*TERRAIN.layer:0,z:c.z+1}}));
  const live={...arena,solids:[...arena.solids.filter(b=>b.kind!=='floor'),...terrainOuterFloors(arena),...floors]};
  liveCache.set(arena,{key,arena:live});return live;
}
